import { BaseDestroy } from 'nera-common/angular';
import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MatDatepickerIntl } from '@angular/material/datepicker';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorIntl } from '@angular/material/paginator';
import {
    LangDefinition,
    TranslocoPipe,
    TranslocoService,
} from '@jsverse/transloco';
import { AppDatepickerIntl } from '../../../core/i18n/app-datepicker-intl';

import { AppPaginatorIntl } from '../../../core/i18n/app-paginator-intl';

import { getAngularLocale } from 'nera-common/angular';
@Component({
    selector: 'languages',
    templateUrl: './languages.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'languages',
    imports: [MatButtonModule, MatMenuModule, NgTemplateOutlet, TranslocoPipe],
})
export class LanguagesComponent extends BaseDestroy implements OnInit {
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    private readonly _translocoService = inject(TranslocoService);
    // Optional: kanban #211/#253 dropped this switcher onto guest pages (sign-in,
    // pricing, error pages, …) that have no date picker or paginator anywhere on
    // them and therefore never provide these tokens — this component's job there
    // is just the locale switch itself, not the app-wide DateAdapter/paginator
    // sync those pages have no use for.
    private readonly _dateAdapter = inject(DateAdapter, { optional: true });
    private readonly _paginator = inject(MatPaginatorIntl, {
        optional: true,
    }) as AppPaginatorIntl | null;
    private readonly _datepicker = inject(MatDatepickerIntl, {
        optional: true,
    }) as AppDatepickerIntl | null;

    availableLangs: LangDefinition[];
    activeLang: string;
    flagCodes: Record<string, string>;

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Get the available languages from transloco
        // Config supplies LangDefinition[] ({id,label}); getAvailableLangs is
        // typed as the string|definition union, so normalize to definitions.
        this.availableLangs = this._translocoService
            .getAvailableLangs()
            .map((l) => (typeof l === 'string' ? { id: l, label: l } : l));

        // Subscribe to language changes.
        //
        // PFL-1841: langChanges$ is a hot, never-completing stream owned by the
        // app-wide TranslocoService, so an unguarded subscribe here outlives the
        // component — every re-created LanguagesComponent (layout swap) left a
        // dead handler still calling setLocale/setLanguage/markForCheck on a
        // destroyed view.
        this._translocoService.langChanges$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((activeLang) => {
                this.activeLang = activeLang;
                this._dateAdapter?.setLocale(getAngularLocale(activeLang));
                // Some test/host contexts register a plain MatPaginatorIntl (no
                // setLanguage) rather than our AppPaginatorIntl subclass — guard
                // the call, not just the injection.
                if (typeof this._paginator?.setLanguage === 'function') {
                    this._paginator.setLanguage(activeLang);
                }
                if (typeof this._datepicker?.setLanguage === 'function') {
                    this._datepicker.setLanguage(activeLang);
                }
                this._changeDetectorRef.markForCheck();
            });

        // Set the country iso codes for languages for flags
        this.flagCodes = {
            en: 'us',
            vn: 'vn',
        };
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set the active lang
     *
     * @param lang
     */
    setActiveLang(lang: string): void {
        // Set the active lang
        localStorage.setItem('appLanguage', lang);
        this._translocoService.setActiveLang(lang);
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(
        index: number,
        item: string | { id?: string | number }
    ): string | number {
        return typeof item === 'string' ? item : (item.id ?? index);
    }
}
